// VENDORED COPY of frontend/src/lib/financial/formulas.ts — do not edit here; keep in sync with the main app.
/**
 * Formula presets and evaluation for financial calculations.
 *
 * This module is the single source of truth for formula evaluation.
 * The window.AppLib.evaluateFormula delegation has been removed --
 * the implementation here IS the primary engine.
 *
 * IMPORTANT: The `^` operator is converted to `Math.pow()` calls
 * (matching the legacy financial-calc.js behaviour) rather than
 * using the JS `**` operator, because `**` has different operator
 * precedence which can produce incorrect results for expressions
 * like `(1 + 10 / 100) ^ period`.
 */

import type { FormulaPreset, FormulaPresetDetection } from './types';

/**
 * Formula preset definitions.
 * These are used for both costs and savings with identical logic.
 */
export const FORMULA_PRESETS: Record<string, FormulaPreset> = {
  fixed: {
    label: 'Fixed',
    description: 'Value stays the same each period',
    formula: 'base',
    icon: 'fixed',
    hasRate: false,
  },
  linear: {
    label: 'Linear',
    description: 'Increases by a fixed amount each period',
    formula: 'base + {rate} * period',
    icon: 'linear',
    hasRate: true,
    rateLabel: 'Amount per period',
    rateStep: '1',
    defaultRate: 50,
  },
  percentage: {
    label: 'Percent',
    description: 'Increases by a percentage each period',
    formula: 'base * (1 + {rate} / 100) ^ period',
    icon: 'percentage',
    hasRate: true,
    rateLabel: 'Growth rate (%)',
    rateStep: '0.1',
    defaultRate: 10,
  },
  compound: {
    label: 'Compound',
    description: 'Each period grows from previous',
    formula: 'prev * (1 + {rate} / 100)',
    icon: 'compound',
    hasRate: true,
    rateLabel: 'Compound rate (%)',
    rateStep: '0.1',
    defaultRate: 5,
  },
  custom: {
    label: 'Custom',
    description: 'Define your own formula using base, period, prev, pow(), sqrt()',
    formula: null,
    icon: 'custom',
    hasRate: false,
  },
};

/**
 * Detect which preset a formula matches.
 *
 * @param formula - Formula string to analyse
 * @returns Preset key and extracted rate (if applicable)
 */
export function detectFormulaPreset(formula: string | null | undefined): FormulaPresetDetection {
  if (!formula || formula === 'base') {
    return { preset: 'fixed', rate: null };
  }

  // Linear: base + X * period
  const linearMatch = formula.match(/^base\s*\+\s*([\d.]+)\s*\*\s*period$/);
  if (linearMatch) {
    return { preset: 'linear', rate: parseFloat(linearMatch[1]) };
  }

  // Percentage: base * (1 + X / 100) ^ period
  const percentMatch = formula.match(/^base\s*\*\s*\(\s*1\s*\+\s*([\d.]+)\s*\/\s*100\s*\)\s*\^\s*period$/);
  if (percentMatch) {
    return { preset: 'percentage', rate: parseFloat(percentMatch[1]) };
  }

  // Compound: prev * (1 + X / 100)
  const compoundMatch = formula.match(/^prev\s*\*\s*\(\s*1\s*\+\s*([\d.]+)\s*\/\s*100\s*\)$/);
  if (compoundMatch) {
    return { preset: 'compound', rate: parseFloat(compoundMatch[1]) };
  }

  // Custom formula
  return { preset: 'custom', rate: null };
}

/**
 * Generate a formula string from a preset and rate value.
 *
 * @param preset - Preset key (fixed, linear, percentage, compound, custom)
 * @param rate - Rate value for presets that require one
 * @returns Formula string ready for evaluation
 */
export function generateFormula(preset: string, rate: number | null): string {
  const presetConfig = FORMULA_PRESETS[preset];
  if (!presetConfig || !presetConfig.formula) {
    return 'base';
  }

  const rateValue = rate ?? presetConfig.defaultRate ?? 0;
  return presetConfig.formula.replace('{rate}', String(rateValue));
}

/**
 * Safe recursive-descent parser for formula expressions.
 *
 * Grammar:
 *   expr    = term (('+' | '-') term)*
 *   term    = power (('*' | '/') power)*
 *   power   = unary ('^' unary)*
 *   unary   = '-' unary | primary
 *   primary = number | variable | func '(' args ')' | '(' expr ')'
 *
 * Supported variables: base, period, prev
 * Supported functions: pow(x, n), sqrt(x)
 */
function parseFormula(
  formula: string,
  vars: Record<string, number>,
): number {
  let pos = 0;
  const src = formula.trim();

  const peek = () => src[pos] ?? '';
  const consume = (ch: string) => {
    skipWs();
    if (src[pos] !== ch) throw new Error(`Expected '${ch}' at pos ${pos}`);
    pos++;
  };
  const skipWs = () => { while (pos < src.length && src[pos] === ' ') pos++; };

  const readNumber = (): number => {
    skipWs();
    let s = '';
    if (src[pos] === '-') { s += '-'; pos++; }
    while (pos < src.length && /[\d.]/.test(src[pos])) { s += src[pos++]; }
    if (!s || s === '-') throw new Error(`Expected number at pos ${pos}`);
    return parseFloat(s);
  };

  const readIdent = (): string => {
    skipWs();
    let s = '';
    while (pos < src.length && /[a-zA-Z_]/.test(src[pos])) { s += src[pos++]; }
    return s;
  };

  // Forward declarations
  let parseExpr: () => number;

  const parsePrimary = (): number => {
    skipWs();
    const ch = peek();

    // Parenthesised sub-expression
    if (ch === '(') {
      pos++;
      const val = parseExpr();
      consume(')');
      return val;
    }

    // Number literal
    if (/[\d.]/.test(ch)) {
      return readNumber();
    }

    // Identifier: variable or function call
    if (/[a-zA-Z_]/.test(ch)) {
      const name = readIdent();
      skipWs();

      // Function call
      if (peek() === '(') {
        pos++; // consume '('
        if (name === 'sqrt') {
          const arg = parseExpr();
          consume(')');
          return Math.sqrt(arg);
        }
        if (name === 'pow') {
          const x = parseExpr();
          consume(',');
          const n = parseExpr();
          consume(')');
          return Math.pow(x, n);
        }
        throw new Error(`Unknown function: ${name}`);
      }

      // Variable
      if (Object.prototype.hasOwnProperty.call(vars, name)) {
        return vars[name];
      }
      throw new Error(`Unknown identifier: ${name}`);
    }

    throw new Error(`Unexpected character '${ch}' at pos ${pos}`);
  };

  const parseUnary = (): number => {
    skipWs();
    if (peek() === '-') { pos++; return -parseUnary(); }
    return parsePrimary();
  };

  const parsePower = (): number => {
    let base = parseUnary();
    skipWs();
    while (peek() === '^') {
      pos++;
      const exp = parseUnary();
      base = Math.pow(base, exp);
      skipWs();
    }
    return base;
  };

  const parseTerm = (): number => {
    let val = parsePower();
    skipWs();
    while (peek() === '*' || peek() === '/') {
      const op = src[pos++];
      const right = parsePower();
      val = op === '*' ? val * right : val / right;
      skipWs();
    }
    return val;
  };

  parseExpr = (): number => {
    let val = parseTerm();
    skipWs();
    while (peek() === '+' || peek() === '-') {
      const op = src[pos++];
      const right = parseTerm();
      val = op === '+' ? val + right : val - right;
      skipWs();
    }
    return val;
  };

  const result = parseExpr();
  skipWs();
  if (pos !== src.length) {
    throw new Error(`Unexpected token at pos ${pos}: '${src.slice(pos)}'`);
  }
  return result;
}

/**
 * Safely evaluate a formula expression for a given period.
 *
 * Supported variables: `base`, `period`, `prev`
 * Supported functions: `pow(x, n)`, `sqrt(x)`
 * Supported operators: `+`, `-`, `*`, `/`, `^` (power)
 *
 * @param formula - Formula string
 * @param base - Base value
 * @param period - Period number (0-indexed from effective date)
 * @param prev - Previous period value
 * @param cap - Optional maximum cap value
 * @returns Calculated value (falls back to `base` on error)
 */
export function evaluateFormula(
  formula: string,
  base: number,
  period: number,
  prev: number,
  cap?: number | null,
): number {
  if (!formula || formula === 'base') {
    return base;
  }

  try {
    const result = parseFormula(formula, { base, period, prev });

    const capValue = cap ?? null;
    if (capValue !== null && result > capValue) {
      return capValue;
    }

    return isNaN(result) ? base : result;
  } catch (err) {
    console.error(
      `[evaluateFormula] Error evaluating formula: ${err instanceof Error ? err.message : String(err)}`,
    );
    return base;
  }
}
